/**
 * Centralized FontAwesome Icon Configuration
 *
 * This file provides a centralized configuration for all FontAwesome icons used in the application.
 * Icons are organized by style (brands, duotone, light, regular, solid) for better maintainability.
 *
 * @author Housefolios
 * @created 2025-01-27
 * @modified 2025-08-12
 */

// Brand icons (free)
export const BRAND_ICONS = [
  'faFacebook',
  'faGoogle',
  'faLinkedin',
  'faTwitter',
]

// Duotone icons (was pro) → approximate with solid
export const DUOTONE_ICONS = ['faBell', 'faRocket', 'faSignHanging']

// Light icons (was pro) → approximate with regular
export const LIGHT_ICONS = [
  'faBell',
  'faFile',
  'faFileChartPie',
  'faFileExcel',
  'faChessQueenAlt',
  'faCommentDots',
  'faCloudDownload',
  'faDatabase',
  'faDivide',
  'faFolder',
  'faHammer',
  'faHammer',
  'faHeart',
  'faInfoCircle',
  'faPaperPlane',
  'faPen',
  'faToolbox',
  'faUserCheck',
  'faUserSlash',
  'faExclamationTriangle',
  'faMapMarkedAlt',
  'faXmark',
]

// Regular icons (free)
export const REGULAR_ICONS = [
  'faThumbsUp',
  'faThumbsDown',
  'faAddressBook',
  'faAddressCard',
  'faAngleDown',
  'faAngleRight',
  'faArrowAltToTop',
  'faArrowLeft',
  'faArrowRight',
  'faArrowUpRightFromSquare',
  'faBars',
  'faBuilding',
  'faChartLineUp',
  'faCheck',
  'faChessRook',
  'faCircle',
  'faClock',
  'faCloudDownload',
  'faCodeCompare',
  'faCommentAltLines',
  'faCommentDots',
  'faCompass',
  'faCopy',
  'faCrosshairs',
  'faDatabase',
  'faDollarSign',
  'faEdit',
  'faEllipsisH',
  'faEllipsisV',
  'faEnvelope',
  'faExternalLink',
  'faFileAlt',
  'faFileChartLine',
  'faFileChartColumn',
  'faFileExcel',
  'faFileExport',
  'faFileInvoiceDollar',
  'faFileTimes',
  'faFileXmark',
  'faFolder',
  'faFolderCog',
  'faFolderOpen',
  'faFolderPlus',
  'faFolderUpload',
  'faFolderXmark',
  'faHandshake',
  'faHomeLg',
  'faImage',
  'faImages',
  'faInfoCircle',
  'faLandmark',
  'faLaptopHouse',
  'faLightbulb',
  'faLink',
  'faLocationExclamation',
  'faMapMarkerExclamation',
  'faMapMarkerPlus',
  'faMoneyCheckEditAlt',
  'faObjectUngroup',
  'faPaperclip',
  'faPen',
  'faPeopleRoof',
  'faPhone',
  'faPlus',
  'faPlusCircle',
  'faPrint',
  'faQuestionCircle',
  'faQuestionSquare',
  'faRocketLaunch',
  'faSave',
  'faSearch',
  'faShare',
  'faShareAlt',
  'faSignOutAlt',
  'faSign',
  'faSignHanging',
  'faSlidersH',
  'faStars',
  'faSortAmountDown',
  'faSquare',
  'faStar',
  'faTimesCircle',
  'faTimes',
  'faTrash',
  'faTrashAlt',
  'faTriangle',
  'faUpToLine',
  'faUser',
  'faUserGraduate',
  'faUserFriends',
  'faUserPlus',
  'faUsersCog',
  'faCartShopping',
  'faHouseCircleCheck',
  'faHouseCircleExclamation',
  'faHouseChimney',
  'faCirclePlay',
]

// Solid icons (free)
export const SOLID_ICONS = [
  'faAward',
  'faAngleDown',
  'faAngleUp',
  'faAsterisk',
  'faArrowDown',
  'faArrowLeft',
  'faArrowRight',
  'faArrowUpRightFromSquare',
  'faArrowsAltH',
  'faBars',
  'faBell',
  'faBox',
  'faBuilding',
  'faChartPie',
  'faCheck',
  'faChevronDown',
  'faChevronLeft',
  'faChevronRight',
  'faCircleInfo',
  'faCloudArrowDown',
  'faCrosshairs',
  'faDatabase',
  'faDollarSign',
  'faEllipsis',
  'faEllipsisV',
  'faExclamation',
  'faEnvelope',
  'faFile',
  'faFileInvoiceDollar',
  'faFire',
  'faBackwardFast',
  'faFolderUpload',
  'faFolderPlus',
  'faGear',
  'faGraduationCap',
  'faGripVertical',
  'faHammer',
  'faHeart',
  'faHome',
  'faHouseChimney',
  'faHouseCircleExclamation',
  'faKey',
  'faLink',
  'faListOl',
  'faMapMarkerCheck',
  'faMapMarker',
  'faMessage',
  'faMoneyCheckDollar',
  // Ensure file-excel is available via solid style as well
  'faFileExcel',
  'faPaperclip',
  'faPaperPlane',
  'faPen',
  'faPeopleRoof',
  'faPhone',
  'faPlay',
  'faPlus',
  'faPrint',
  'faSearch',
  'faShareNodes',
  'faRightFromBracket',
  'faSign',
  'faSignHanging',
  'faSlidersH',
  'faSliders',
  'faLocationDot',
  'faTimes',
  'faRocket',
  'faTrash',
  'faTrashCan',
  'faUniversity',
  'faUpload',
  'faUserGraduate',
  'faUserGroup',
  'faUserPlus',
  'faVideo',
  'faWallet',
  'faWandMagicSparkles',
]

// Icon style mapping for library registration
export const ICON_STYLES = {
  brands: BRAND_ICONS,
  duotone: DUOTONE_ICONS,
  light: LIGHT_ICONS,
  regular: REGULAR_ICONS,
  solid: SOLID_ICONS,
}

// All icons flattened for easy access
export const ALL_ICONS = [
  ...BRAND_ICONS,
  ...DUOTONE_ICONS,
  ...LIGHT_ICONS,
  ...REGULAR_ICONS,
  ...SOLID_ICONS,
]

// Export icon library functions
export {
  initializeIconLibrarySync,
  registerIconStyleSync,
  registerSingleIconSync,
  getTotalIconCount,
  getIconCountsByStyle,
} from './iconLibrarySync'

export {
  ICON_ALIASES,
  ICON_LEGACY_NAMES,
  faExportToIconName,
} from './iconAliases'

// Export async version for future use
export {
  initializeIconLibrary as initializeIconLibraryAsync,
  registerIconStyle as registerIconStyleAsync,
  registerSingleIcon as registerSingleIconAsync,
} from './iconLibrary'
